import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Image,
} from 'react-native';
import { Alert } from '../../utils/toast';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../theme/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import ThemedText from '../common/ThemedText';
import { ClubsStackParamList } from '../../types/navigation';
import { getMeetups, acceptMeetup, rejectMeetup, Meetup } from '../../lib/api/meetups';

interface ClubMeetupsTabProps {
  clubId: string;
  onCreateMeetup?: () => void;
}

type ProfileStackParamList = {
  Profile: { userId: string };
};

type ClubsNavigationProp = StackNavigationProp<ClubsStackParamList>;

const ClubMeetupsTab: React.FC<ClubMeetupsTabProps> = ({ clubId, onCreateMeetup }) => {
  const navigation = useNavigation<any>();
  const { theme } = useTheme();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [meetups, setMeetups] = useState<Meetup[]>([]);

  useEffect(() => {
    loadMeetups();
  }, [clubId]);

  const loadMeetups = async () => {
    try {
      setLoading(true);
      const data = await getMeetups(clubId);
      // Combine sent and received, sort by date
      const allMeetups = [...data.sent, ...data.received].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setMeetups(allMeetups);
    } catch (error) {
      console.error('Error loading meetups:', error);
      Alert.alert('Error', 'Failed to load meetups');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadMeetups();
  };

  const handleAccept = async (id: string) => {
    try {
      await acceptMeetup(id);
      Alert.alert('Success', 'Meetup accepted');
      loadMeetups();
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to accept meetup');
    }
  };

  const handleReject = async (id: string) => {
    try {
      await rejectMeetup(id);
      Alert.alert('Success', 'Meetup rejected');
      loadMeetups();
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to reject meetup');
    }
  };

  const renderMeetup = (meetup: Meetup) => {
    // Check if this is a sent or received meetup based on current user
    const isSent = meetup.creatorId === user?.userId;
    const otherUser = isSent ? meetup.recipient : meetup.creator;
    const isPending = meetup.status === 'pending';
    const isAccepted = meetup.status === 'accepted';
    const isRejected = meetup.status === 'rejected';

    return (
      <View
        key={meetup.id}
        style={[styles.meetupCard, { backgroundColor: theme.card, borderColor: theme.border, shadowColor: theme.shadow }]}
      >
        {/* Header with User Info and Status */}
        <View style={styles.cardHeader}>
          <TouchableOpacity 
            style={styles.meetupHeaderRow}
            onPress={() => navigation.navigate('Profile', { userId: otherUser.id })}
            activeOpacity={0.7}
          >
            <View style={[styles.avatar, { backgroundColor: theme.backgroundSecondary }]}>
              {otherUser.photo ? (
                <Image 
                  source={{ uri: otherUser.photo }} 
                  style={styles.avatarImage}
                />
              ) : (
                <Ionicons name="person" size={24} color={theme.textSecondary} />
              )}
            </View>
            <View style={styles.userInfoBlock}>
              <View style={styles.userNameRow}>
                <ThemedText style={[styles.userName, { color: theme.text }]}>
                  {otherUser.name || 'Unknown User'}
                </ThemedText>
                <View style={[styles.directionBadge, { backgroundColor: isSent ? theme.accent + '15' : '#10B981' + '15' }]}>
                  <Ionicons 
                    name={isSent ? 'arrow-forward' : 'arrow-back'} 
                    size={12} 
                    color={isSent ? theme.accent : '#10B981'} 
                  />
                  <ThemedText style={[styles.directionText, { color: isSent ? theme.accent : '#10B981' }]}>
                    {isSent ? 'Sent' : 'Received'}
                  </ThemedText>
                </View>
              </View>
              {otherUser.profession && (
                <ThemedText style={[styles.userProfession, { color: theme.textSecondary }]}>
                  {otherUser.profession}
                </ThemedText>
              )}
            </View>
          </TouchableOpacity>
          
          <View
            style={[
              styles.statusBadge,
              {
                backgroundColor: isPending
                  ? theme.accent + '20'
                  : isAccepted
                  ? '#10B981' + '20'
                  : '#EF4444' + '20',
              },
            ]}
          >
            <ThemedText
              style={[
                styles.statusText,
                {
                  color: isPending ? theme.accent : isAccepted ? '#10B981' : '#EF4444',
                },
              ]}
            >
              {meetup.status.charAt(0).toUpperCase() + meetup.status.slice(1)}
            </ThemedText>
          </View>
        </View>

        {/* Divider */}
        <View style={[styles.divider, { backgroundColor: theme.border }]} />

        {/* Meetup Details */}
        <View style={styles.detailsContainer}>
          {meetup.dateTime && (
            <View style={styles.detailRow}>
              <Ionicons name="calendar" size={16} color={theme.textSecondary} />
              <ThemedText style={[styles.detailText, { color: theme.text }]}>
                {new Date(meetup.dateTime).toLocaleDateString()} at {new Date(meetup.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </ThemedText>
            </View>
          )}
          <View style={styles.detailRow}>
            <Ionicons name="business" size={16} color={theme.textSecondary} />
            <ThemedText style={[styles.detailText, { color: theme.text }]}>
              {meetup.place}
            </ThemedText>
          </View>
          <View style={styles.detailRowSplit}>
            <View style={styles.detailRowHalf}>
              <Ionicons name="call" size={16} color={theme.textSecondary} />
              <ThemedText style={[styles.detailText, { color: theme.text }]}>
                {meetup.phone}
              </ThemedText>
            </View>
            <View style={styles.detailRowHalf}>
              <Ionicons name="people" size={16} color={theme.textSecondary} />
              <ThemedText style={[styles.detailText, { color: theme.text }]}>
                {meetup.type}
              </ThemedText>
            </View>
          </View>
        </View>

        {/* Message */}
        {meetup.message && (
          <View style={[styles.messageContainer, { backgroundColor: theme.background }]}>
            <ThemedText style={[styles.message, { color: theme.textSecondary }]}>
              {meetup.message}
            </ThemedText>
          </View>
        )}

        {/* Action Buttons (for received pending meetups) */}
        {!isSent && isPending && (
          <View style={styles.actions}>
            <TouchableOpacity
              style={[styles.actionButton, { backgroundColor: '#10B981' }]}
              onPress={() => handleAccept(meetup.id)}
              activeOpacity={0.7}
            >
              <Ionicons name="checkmark" size={20} color="#FFFFFF" />
              <ThemedText style={styles.actionButtonText}>Accept</ThemedText>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.actionButton, { backgroundColor: '#EF4444' }]}
              onPress={() => handleReject(meetup.id)}
              activeOpacity={0.7}
            >
              <Ionicons name="close" size={20} color="#FFFFFF" />
              <ThemedText style={styles.actionButtonText}>Reject</ThemedText>
            </TouchableOpacity>
          </View>
        )}
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.accent} />
      </View>
    );
  }

  // Calculate stats
  const sentMeetups = meetups.filter(m => m.creatorId === user?.userId);
  const receivedMeetups = meetups.filter(m => m.creatorId !== user?.userId);
  const pendingMeetups = meetups.filter(m => m.status === 'pending');
  const acceptedMeetups = meetups.filter(m => m.status === 'accepted');

  return (
    <View style={styles.container}>
      {/* Stats Bar */}
      {meetups.length > 0 && (
        <View style={[styles.statsBar, { backgroundColor: theme.card, borderBottomColor: theme.border }]}>
          <View style={styles.statItem}>
            <ThemedText style={[styles.statValue, { color: theme.accent }]}>
              {sentMeetups.length}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
              Sent
            </ThemedText>
          </View>
          <View style={[styles.statDivider, { backgroundColor: theme.border }]} />
          <View style={styles.statItem}>
            <ThemedText style={[styles.statValue, { color: '#10B981' }]}>
              {receivedMeetups.length}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
              Received
            </ThemedText>
          </View>
          <View style={[styles.statDivider, { backgroundColor: theme.border }]} />
          <View style={styles.statItem}>
            <ThemedText style={[styles.statValue, { color: '#F59E0B' }]}>
              {pendingMeetups.length}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
              Pending
            </ThemedText>
          </View>
          <View style={[styles.statDivider, { backgroundColor: theme.border }]} />
          <View style={styles.statItem}>
            <ThemedText style={[styles.statValue, { color: '#10B981' }]}>
              {acceptedMeetups.length}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
              Accepted
            </ThemedText>
          </View>
        </View>
      )}

      {/* Meetups List */}
      <ScrollView
        style={styles.scrollView}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={theme.accent} />
        }
      >
        {meetups.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Ionicons name="calendar-outline" size={64} color={theme.textSecondary} />
            <ThemedText style={[styles.emptyText, { color: theme.textSecondary }]}>
              No meetup requests yet
            </ThemedText>
            <ThemedText style={[styles.emptySubtext, { color: theme.textTertiary }]}>
              Tap + to send a meetup request
            </ThemedText>
          </View>
        ) : (
          meetups.map((meetup) => renderMeetup(meetup))
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollView: {
    flex: 1,
    paddingBottom: 80,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 80,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    marginTop: 8,
  },
  meetupCard: {
    marginHorizontal: 16,
    marginTop: 16,
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  meetupHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 8,
  },
  userInfoBlock: {
    flex: 1,
  },
  userNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2,
  },
  divider: {
    height: 1,
    marginBottom: 12,
    opacity: 0.5,
  },
  directionBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    gap: 3,
  },
  directionText: {
    fontSize: 11,
    fontWeight: '600',
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 24,
  },
  userName: {
    fontSize: 16,
    fontWeight: '700',
  },
  userProfession: {
    fontSize: 13,
    marginTop: 2,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  detailsContainer: {
    gap: 8,
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  detailRowSplit: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  detailRowHalf: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  detailText: {
    fontSize: 14,
    flex: 1,
  },
  messageContainer: {
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  message: {
    fontSize: 14,
    lineHeight: 20,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: 8,
    gap: 6,
  },
  actionButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  timestamp: {
    fontSize: 12,
    textAlign: 'right',
  },
  statsBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
  },
  statValue: {
    fontSize: 24,
    fontWeight: '700',
  },
  statLabel: {
    fontSize: 12,
    marginTop: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  statDivider: {
    width: 1,
    height: 32,
  },
});

export default ClubMeetupsTab;
